// src/components/Editor/DocLayout.jsx
//
// Standalone layout for /d/:docId — a minimal single-document view with:
//   - Nav bar in 'doc' mode (print + download icons, no project/versions)
//   - A single EditorPanel instance (no TabShelf, no left panel, no VersionPanel)
//   - Read-only or editable based on the user's project membership
//
// This doubles as the print view — @media print CSS hides the Nav strip.

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import Nav from '../SidePanel/Nav';
import EditorPanel from '../EditorPanel/EditorPanel';
import Tooltip from '../Overlays/Tooltip';
import { ViewModeProvider } from '../../state/ViewModeContext';
import { canEditProjectContent, getViewMode } from '../../helpers/projectAccess';
import { supabase } from '../../supabaseClient';
import { debug } from '../../helpers/debug';

export default function DocLayout({ user }) {
  const { docId: docIdStr } = useParams();
  const [searchParams] = useSearchParams();
  const docId = Number(docIdStr);

  const [doc, setDoc] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tabs, setTabs] = useState([]);
  const editorPanelRef = useRef();
  const hasAutoPrintedRef = useRef(false);

  useEffect(() => {
    if (!docId || !user?.id) return;

    setLoading(true);
    setNotFound(false);

    (async () => {
      try {
        // 1. Fetch doc metadata + project_id via project_docs join
        const { data: docData, error: docError } = await supabase
          .from('docs')
          .select('id, name, layout, updated_at, project_docs(project_id)')
          .eq('id', docId)
          .maybeSingle();

        if (docError || !docData) {
          debug('[DocLayout] Doc not found:', { type: 'warn' }, { docId, error: docError });
          setNotFound(true);
          setLoading(false);
          return;
        }

        setDoc(docData);

        const projectId = docData.project_docs?.[0]?.project_id;
        if (projectId) {
          const { apiGetProjects } = await import('../../api/projects');
          const list = await apiGetProjects(user.id, user.email);
          const proj = (list || []).find(p => Number(p.id) === Number(projectId)) || null;
          setProject(proj);

          if (!proj) {
            // User is not a member of the project that owns this doc
            debug('[DocLayout] Not a member of project:', { type: 'warn' }, { projectId });
            setNotFound(true);
            setLoading(false);
            return;
          }
        }

        // Build a single virtual tab for this doc
        const virtualTab = {
          id: `doc-tab-${docId}`,
          docId,
          label: docData.name || 'Untitled',
          pane: 'left'
        };
        setTabs([virtualTab]);
      } catch (err) {
        debug('[DocLayout] Load error:', { type: 'error' }, err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [docId, user?.id, user?.email]);

  // Keep the tab label in sync when doc name is known
  useEffect(() => {
    if (!doc) return;
    setTabs(prev => prev.map(t =>
      t.docId === docId ? { ...t, label: doc.name || 'Untitled' } : t
    ));
  }, [doc, docId]);

  // Update document title
  useEffect(() => {
    if (doc?.name) document.title = `${doc.name} | Ghost`;
    else if (docId) document.title = 'Document | Ghost';
    return () => { document.title = 'Ghost'; };
  }, [doc?.name, docId]);

  // Optional direct print trigger: /d/:docId?print=1
  useEffect(() => {
    if (loading || notFound) return;
    if (hasAutoPrintedRef.current) return;

    const printParam = String(searchParams.get('print') || '').toLowerCase();
    const shouldAutoPrint = printParam === '1' || printParam === 'true' || printParam === 'yes';
    if (!shouldAutoPrint) return;

    hasAutoPrintedRef.current = true;
    let cancelled = false;
    let timer = null;

    const attemptAutoPrint = (attempt = 0) => {
      if (cancelled) return;

      const maxAttempts = 50;
      const editorApi = editorPanelRef.current;
      const stateByDoc = editorApi?.getEditorStates?.() || {};
      const state = stateByDoc[docId] || stateByDoc[String(docId)] || stateByDoc[Number(docId)] || null;
      const editor = editorApi?.getActiveEditor?.() || null;

      const expectedJson = state?.content?.json || state?.content || null;
      const expectedNodeCount = Array.isArray(expectedJson?.content) ? expectedJson.content.length : 0;
      const proseMirror = document.querySelector(`#ghost-${docId} .ProseMirror`);

      let editorNodeCount = 0;
      try {
        const currentJson = editor?.getJSON?.();
        editorNodeCount = Array.isArray(currentJson?.content) ? currentJson.content.length : 0;
      } catch (err) {
        editorNodeCount = 0;
      }

      const hasEditorMounted = Boolean(editor && proseMirror);
      const hasExpectedContent = expectedNodeCount > 0;
      const hasVisibleContent = editorNodeCount > 0 || (proseMirror?.textContent || '').trim().length > 0;
      const canPrint = hasEditorMounted && (hasVisibleContent || !hasExpectedContent);

      if (canPrint) {
        requestAnimationFrame(() => {
          if (cancelled) return;
          window.setTimeout(() => {
            if (cancelled) return;
            try {
              window.print();
            } catch (err) {
              debug('[DocLayout] Auto print failed:', { type: 'warn' }, err);
            }
          }, 30);
        });
        return;
      }

      if (attempt >= maxAttempts) {
        debug('[DocLayout] Auto print readiness timeout; printing anyway', { type: 'warn' }, { docId, attempt });
        try {
          window.print();
        } catch (err) {
          debug('[DocLayout] Auto print failed:', { type: 'warn' }, err);
        }
        return;
      }

      timer = setTimeout(() => attemptAutoPrint(attempt + 1), 120);
    };

    timer = setTimeout(() => attemptAutoPrint(0), 30);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [loading, notFound, searchParams, docId]);

  // Print guard: neutralize any inline shrink/transform styles during print
  useEffect(() => {
    if (!docId) return undefined;

    const wrapperId = `ghost-${docId}`;
    let previousTransform = null;
    let previousTransformOrigin = null;
    let previousShrinkAttr = null;

    const applyPrintSafeState = () => {
      try {
        const wrapper = document.getElementById(wrapperId);
        if (!wrapper) return;

        previousTransform = wrapper.style.transform;
        previousTransformOrigin = wrapper.style.transformOrigin;
        previousShrinkAttr = wrapper.getAttribute('data-shrink-to-fit');

        wrapper.style.setProperty('transform', 'none', 'important');
        wrapper.style.setProperty('transform-origin', 'initial', 'important');
        wrapper.removeAttribute('data-shrink-to-fit');
      } catch (err) {}
    };

    const restoreState = () => {
      try {
        const wrapper = document.getElementById(wrapperId);
        if (!wrapper) return;

        wrapper.style.removeProperty('transform');
        wrapper.style.removeProperty('transform-origin');

        if (previousTransform) wrapper.style.transform = previousTransform;
        if (previousTransformOrigin) wrapper.style.transformOrigin = previousTransformOrigin;
        if (previousShrinkAttr !== null && previousShrinkAttr !== undefined) {
          wrapper.setAttribute('data-shrink-to-fit', previousShrinkAttr);
        }
      } catch (err) {}
    };

    window.addEventListener('beforeprint', applyPrintSafeState);
    window.addEventListener('afterprint', restoreState);

    return () => {
      window.removeEventListener('beforeprint', applyPrintSafeState);
      window.removeEventListener('afterprint', restoreState);
    };
  }, [docId]);

  if (!user) return <Navigate to="/auth" replace />;
  if (loading) return <div id="app"><div /></div>;
  if (notFound) return (
    <div id="app">
      <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>
        Document not found or you don't have access.
      </div>
    </div>
  );

  const isReadOnly = project ? !canEditProjectContent(project) : true;
  const viewMode = project ? getViewMode(project) : 'viewer';
  const activeTabId = tabs[0]?.id || null;

  return (
    <ViewModeProvider viewMode={viewMode}>
      <div id="app" className="doc-only">
        <Tooltip />
        <Nav
          navMode="doc"
          user={user}
          activeTabId={activeTabId}
          tabs={tabs}
          activePanel={null}
          setActivePanel={() => {}}
        />

        {/* No TabShelf, no leftPanel, no VersionPanel */}
        <div id="main" className="doc-only-main">
          <EditorPanel
            ref={editorPanelRef}
            tabs={tabs}
            allTabs={tabs}
            setTabs={setTabs}
            activeTabId={activeTabId}
            currentUserId={user?.id}
            selectedDocId={docId}
            onEditorChange={() => {}}
            dropInEditor={undefined}
            draggedId={null}
            currentProject={project}
            itemsMap={new Map()}
            setItemsMap={() => {}}
            setLeftActiveTabId={() => {}}
            setRightActiveTabId={() => {}}
            tabsRestored={true}
            isProjectReadOnly={isReadOnly}
            pane="left"
            setActivePane={() => {}}
            onRestoreVersion={undefined}
            onRestoreVersionCopy={undefined}
            onVersionCreated={undefined}
            onOpenVersion={undefined}
          />
        </div>
      </div>
    </ViewModeProvider>
  );
}
